/**
 * Pure color resolver for dashboard chart datasets.
 *
 * The agent tags a dataset with a declarative `__coeffColor` intent (see {@link ColorIntent});
 * this resolver maps that intent to concrete color values against the live theme palette + the
 * mode-resolved semantic tokens. It is deliberately PURE — no `window`, no `Chart`, no DOM — so
 * every branch is unit-testable. `createChart` (Step 1.5) owns the impure side (reading the theme
 * off the window, applying fill opacity / border-width) and just assigns whatever this returns.
 *
 * Design notes:
 * - This resolver decides HUE ASSIGNMENT only. Bar/line fill opacity and the
 *   transparent-border-when-borderWidth-0 rule stay in `createChart`, which has those theme knobs.
 *   The one exception is `shades`, whose whole point is an alpha ramp, so it emits rgba directly.
 * - Unknown / unhandled intent strings never throw — they fall back to `auto`.
 */

import type { ColorIntent } from "./types";

/** A single color, or one color per chart element (e.g. per pie segment / per bar). */
export type ChartElementColor = string | string[];

/** Mode-resolved semantic tokens (palette-independent green/red/gray for the active mode). */
export interface SemanticTokens {
  positive: string;
  negative: string;
  neutral: string;
}

export interface ResolveDatasetColorsParams {
  /** The dataset's `__coeffColor` intent. Unknown strings are tolerated and treated as `auto`. */
  intent: ColorIntent | string | undefined;
  /** The theme's 12-tuple `colorPalette`. `palette[0]` is the primary/accent. */
  palette: readonly string[];
  /** Mode-resolved semantic tokens for the current light/dark mode. */
  tokens: SemanticTokens;
  /** Chart.js chart type (e.g. "bar", "line", "pie", "doughnut", "funnel"). */
  chartType: string;
  /** Total number of datasets/series in the chart. */
  seriesCount: number;
  /** This dataset's index within the chart. */
  seriesIndex: number;
  /** Number of data points in this dataset (element count for element-colored charts). */
  dataLength: number;
  /**
   * Whether the dashboard uses the new theme model (`palette:*` / `custom:*`). Gates the
   * Phase-B single-series-bar default: new model → single(primary); legacy → rainbow(rotate).
   */
  isNewModel: boolean;
}

/** Concrete colors to assign, or `{ skip: true }` to leave the dataset's colors untouched. */
export type ResolvedColors =
  | { backgroundColor: ChartElementColor; borderColor: ChartElementColor }
  | { skip: true };

const FALLBACK_COLOR = "#4f46e5";

/** Pure hex → rgba. Reimplemented locally to keep this module dependency-light. */
function hexToRgba(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/** Index into the palette with safe wrap-around; never returns undefined. */
function paletteAt(palette: readonly string[], index: number): string {
  if (palette.length === 0) {
    return FALLBACK_COLOR;
  }
  const i = ((index % palette.length) + palette.length) % palette.length;
  return palette[i];
}

/**
 * Element-colored charts assign one color PER DATA POINT rather than per series:
 * pie/doughnut/funnel always, and any single-series chart (one bar/line per category).
 */
function isElementColored(chartType: string, seriesCount: number): boolean {
  return (
    chartType === "pie" || chartType === "doughnut" || chartType === "funnel" || seriesCount <= 1
  );
}

/** One palette color per element, cycling the palette across `count` elements. */
function rotateElements(palette: readonly string[], count: number): ResolvedColors {
  const n = count > 0 ? count : palette.length;
  const colors = Array.from({ length: n }, (_, i) => paletteAt(palette, i));
  return { backgroundColor: colors, borderColor: colors };
}

/** A single solid color for every element/series. */
function solid(color: string): ResolvedColors {
  return { backgroundColor: color, borderColor: color };
}

/**
 * `auto` mirrors the per-chart-type default in dashboardHelpers `createChart`, plus the Phase-B
 * gate for single-series bars:
 *   - pie/doughnut/funnel → rotate the palette across segments,
 *   - single-series bar → single(primary) under the new model, legacy rainbow otherwise,
 *   - multi-series bar / line / anything else → pin to the series' palette slot.
 */
function resolveAuto(params: ResolveDatasetColorsParams): ResolvedColors {
  const { palette, chartType, seriesCount, seriesIndex, dataLength, isNewModel } = params;

  if (chartType === "pie" || chartType === "doughnut" || chartType === "funnel") {
    return rotateElements(palette, dataLength);
  }

  if (chartType === "bar" && seriesCount <= 1) {
    if (isNewModel) {
      return solid(paletteAt(palette, 0));
    }
    return rotateElements(palette, dataLength);
  }

  return solid(paletteAt(palette, seriesIndex));
}

/**
 * Resolve a dataset's declarative color intent into concrete Chart.js color values.
 *
 * Pure: identical inputs always yield identical outputs. Returns `{ skip: true }` for `fixed`
 * (caller keeps the dataset's existing, deliberately-chosen colors). Unknown intents fall back
 * to `auto` and never throw.
 */
export function resolveDatasetColors(params: ResolveDatasetColorsParams): ResolvedColors {
  const { intent, palette, tokens, seriesCount, seriesIndex, dataLength, chartType } = params;

  switch (intent) {
    case "fixed":
      // User-pinned or agent-fixed colors: leave the dataset alone.
      return { skip: true };

    case "single":
      return solid(paletteAt(palette, 0));

    case "shades": {
      // Monochrome ramp of the primary via a descending alpha ramp across elements.
      const primary = paletteAt(palette, 0);
      const n = dataLength > 0 ? dataLength : palette.length;
      const bg = Array.from({ length: n }, (_, i) => {
        const alpha = n <= 1 ? 1 : 1 - (i / (n - 1)) * 0.7;
        return hexToRgba(primary, Number(alpha.toFixed(3)));
      });
      return { backgroundColor: bg, borderColor: primary };
    }

    case "rotate":
      // Cycle the palette: per element for element-colored charts, else per series slot.
      return isElementColored(chartType, seriesCount)
        ? rotateElements(palette, dataLength)
        : solid(paletteAt(palette, seriesIndex));

    case "slot":
      // Pin to this series' palette slot across the full 12-color range.
      return solid(paletteAt(palette, seriesIndex));

    case "firstN": {
      // Restrict to the first N (= seriesCount) palette slots, then repeat.
      const n = seriesCount > 0 ? Math.min(seriesCount, palette.length || seriesCount) : 1;
      return solid(paletteAt(palette, seriesIndex % n));
    }

    case "emphasis":
      // Highlight the first series with the primary; mute the rest to the neutral token.
      return solid(seriesIndex === 0 ? paletteAt(palette, 0) : tokens.neutral);

    case "positive":
      return solid(tokens.positive);

    case "negative":
      return solid(tokens.negative);

    case "neutral":
      return solid(tokens.neutral);

    case "auto":
      return resolveAuto(params);

    default:
      // Unknown / unhandled intent → safe fallback, never throw.
      return resolveAuto(params);
  }
}
